import type {
  Avatar,
  ChatMessage,
  Invitee,
  Player,
  PlayerPublic,
  QuestionHost,
  QuizEvent,
  SessionState,
  SessionSummary,
  User,
} from '@/api/types';

export const MOCK_USER: User = {
  id: '11111111-1111-4111-8111-111111111111',
  email: 'host@quiz.massimo.click',
  displayName: 'Mads',
  createdAt: new Date().toISOString(),
};

export const MOCK_AVATARS: Avatar[] = [
  { id: 'fox', label: 'Fox', emoji: '🦊' },
  { id: 'cat', label: 'Cat', emoji: '🐱' },
  { id: 'dog', label: 'Dog', emoji: '🐶' },
  { id: 'panda', label: 'Panda', emoji: '🐼' },
  { id: 'unicorn', label: 'Unicorn', emoji: '🦄' },
  { id: 'tiger', label: 'Tiger', emoji: '🐯' },
  { id: 'koala', label: 'Koala', emoji: '🐨' },
  { id: 'octopus', label: 'Octopus', emoji: '🐙' },
  { id: 'frog', label: 'Frog', emoji: '🐸' },
  { id: 'penguin', label: 'Penguin', emoji: '🐧' },
  { id: 'owl', label: 'Owl', emoji: '🦉' },
  { id: 'crab', label: 'Crab', emoji: '🦀' },
  { id: 'avocado', label: 'Avocado', emoji: '🥑' },
  { id: 'pizza', label: 'Pizza', emoji: '🍕' },
  { id: 'donut', label: 'Donut', emoji: '🍩' },
  { id: 'cake', label: 'Cake', emoji: '🍰' },
  { id: 'rocket', label: 'Rocket', emoji: '🚀' },
  { id: 'unicycle', label: 'Star', emoji: '⭐' },
  { id: 'rainbow', label: 'Rainbow', emoji: '🌈' },
  { id: 'flame', label: 'Flame', emoji: '🔥' },
  { id: 'cherry', label: 'Cherry', emoji: '🍒' },
  { id: 'sushi', label: 'Sushi', emoji: '🍣' },
  { id: 'cactus', label: 'Cactus', emoji: '🌵' },
  { id: 'crown', label: 'Crown', emoji: '👑' },
  { id: 'mushroom', label: 'Mushroom', emoji: '🍄' },
  { id: 'bee', label: 'Bee', emoji: '🐝' },
  { id: 'whale', label: 'Whale', emoji: '🐳' },
  { id: 'mage', label: 'Mage', emoji: '🧙' },
  { id: 'ghost', label: 'Ghost', emoji: '👻' },
  { id: 'alien', label: 'Alien', emoji: '👽' },
];

const EVENT_A: QuizEvent = {
  id: 'aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaa1',
  title: "Sara's Bachelorette",
  subject: 'Things about the bride',
  mode: 'invited',
  status: 'ready',
  createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
  questionCount: 12,
  inviteeCount: 7,
  sessionCount: 1,
  collectionPrompt: 'Write 5 funny multiple-choice questions about Sara, the bride-to-be.',
  questionsPerInvitee: 5,
  deadlineAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 2).toISOString(),
  defaultQuestionDurationMs: 20000,
  allowContributorDurationOverride: true,
  shuffle: true,
  revealAuthorship: 'at_end',
  hostRole: 'blind',
  hostPlays: true,
  theme: 'bachelorette',
  allowImages: true,
};

const EVENT_B: QuizEvent = {
  id: 'aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaa2',
  title: "Grandma's 80th",
  subject: 'Family trivia',
  mode: 'solo',
  status: 'draft',
  createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
  questionCount: 4,
  sessionCount: 0,
  defaultQuestionDurationMs: 25000,
  allowContributorDurationOverride: false,
  shuffle: true,
  revealAuthorship: 'never',
  hostRole: 'curator',
  hostPlays: false,
  theme: 'birthday',
  allowImages: true,
};

export const MOCK_EVENTS: QuizEvent[] = [EVENT_A, EVENT_B];

function q(i: number, evId: string, stem: string, opts: Array<[string, boolean?]>): QuestionHost {
  return {
    id: `qqqqqqqq-qqqq-4qqq-8qqq-${String(i).padStart(12, '0')}`,
    eventId: evId,
    type: 'quiz',
    stem,
    position: i,
    options: opts.map(([text, isCorrect], pos) => ({
      id: `oooooooo-oooo-4ooo-8ooo-${String(i * 10 + pos).padStart(12, '0')}`,
      position: pos,
      text,
      isCorrect: !!isCorrect,
    })),
    createdAt: new Date().toISOString(),
    authorDisplayName: ['Mads', 'Aunt Lise', 'Cousin Erik', 'Mum'][i % 4],
  };
}

export const MOCK_QUESTIONS: QuestionHost[] = [
  q(0, EVENT_A.id, "Sara's first job was…", [
    ['Lifeguard at the pool'],
    ['Barista', true],
    ['Babysitter for a chaotic dog'],
    ['Door-to-door candle salesperson'],
  ]),
  q(1, EVENT_A.id, "Sara's secret talent is…", [
    ['Yodelling'],
    ['Naming every county in Ireland'],
    ['Solving a Rubik’s cube in under two minutes', true],
    ['Drawing portraits of strangers on the bus'],
  ]),
  q(2, EVENT_A.id, 'On her first date with Tom she ordered…', [
    ['A bowl of chips', true],
    ['Two desserts, no main'],
    ['Whatever Tom was having'],
    ['Lobster, like a maniac'],
  ]),
  q(3, EVENT_A.id, 'Her travel rule is…', [
    ['Always pack a swimsuit'],
    ['Only carry-on, never check', true],
    ['One book per day'],
    ['Walk twenty thousand steps a day or it doesn’t count'],
  ]),
  q(4, EVENT_A.id, 'Her go-to karaoke song?', [
    ['Wonderwall'],
    ['I Will Survive'],
    ['Mr. Brightside', true],
    ['Anything by ABBA'],
  ]),
];

const PLAYERS: PlayerPublic[] = [
  {
    id: 'p-1',
    nickname: 'Mads (host)',
    avatar: 'fox',
    isHost: true,
    joinedAt: new Date().toISOString(),
  },
  { id: 'p-2', nickname: 'Aunt Lise', avatar: 'unicorn', joinedAt: new Date().toISOString() },
  { id: 'p-3', nickname: 'Cousin Erik', avatar: 'panda', joinedAt: new Date().toISOString() },
  { id: 'p-4', nickname: 'Mum', avatar: 'cat', joinedAt: new Date().toISOString() },
  { id: 'p-5', nickname: 'Tom', avatar: 'tiger', joinedAt: new Date().toISOString() },
];

export const MOCK_PLAYERS = PLAYERS;

export const MOCK_INVITEES: Invitee[] = [
  {
    id: 'inv-1',
    eventId: EVENT_A.id,
    name: 'Aunt Lise',
    email: 'lise@family.example',
    status: 'submitted',
    submittedAt: new Date().toISOString(),
    questionCount: 5,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'inv-2',
    eventId: EVENT_A.id,
    name: 'Cousin Erik',
    email: 'erik@family.example',
    status: 'opened',
    questionCount: 2,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'inv-3',
    eventId: EVENT_A.id,
    name: 'Mum',
    email: null,
    status: 'pending',
    questionCount: 0,
    createdAt: new Date().toISOString(),
  },
];

export const MOCK_SESSION_ID = 'ssssssss-ssss-4sss-8sss-ssssssssssss';

export const MOCK_LOBBY_STATE: SessionState = {
  sessionId: MOCK_SESSION_ID,
  pin: '123456',
  status: 'lobby',
  theme: 'default',
  version: 3,
  totalQuestions: MOCK_QUESTIONS.length,
  players: PLAYERS,
  leaderboard: [],
  progressionMode: 'kiosk',
};

// All MOCK_QUESTIONS are quiz-typed; narrow once here so the
// fixtures below can read `Q0.options` without optional chaining.
const Q0Raw = MOCK_QUESTIONS[0]!;
if (Q0Raw.type !== 'quiz') {
  throw new Error('MOCK_QUESTIONS[0] is expected to be a quiz question');
}
const Q0 = Q0Raw;

export const MOCK_QUESTION_OPEN_STATE: SessionState = {
  sessionId: MOCK_SESSION_ID,
  pin: '123456',
  status: 'question_open',
  theme: 'default',
  version: 7,
  progressionMode: 'kiosk',
  currentIndex: 0,
  totalQuestions: MOCK_QUESTIONS.length,
  players: PLAYERS,
  leaderboard: PLAYERS.map((p, i) => ({
    playerId: p.id,
    nickname: p.nickname,
    avatar: p.avatar,
    isHost: p.isHost,
    score: 800 - i * 80,
    rank: i + 1,
  })),
  currentQuestionKiosk: {
    questionId: Q0.id,
    type: 'quiz',
    index: 0,
    total: MOCK_QUESTIONS.length,
    stem: Q0.stem,
    options: Q0.options.map((o) => ({ id: o.id, position: o.position, text: o.text })),
    durationMs: 20000,
    startedAt: new Date().toISOString(),
    endsAt: new Date(Date.now() + 20000).toISOString(),
  },
  currentQuestionPlayer: {
    questionId: Q0.id,
    type: 'quiz',
    index: 0,
    total: MOCK_QUESTIONS.length,
    optionsCount: Q0.options.length,
    options: Q0.options.map((o) => ({ id: o.id, position: o.position })),
    durationMs: 20000,
    startedAt: new Date().toISOString(),
    endsAt: new Date(Date.now() + 20000).toISOString(),
  },
};

const LEADERBOARD_AFTER_Q1 = PLAYERS.map((p, i) => ({
  playerId: p.id,
  nickname: p.nickname,
  avatar: p.avatar,
  isHost: p.isHost,
  score: 920 - i * 110,
  rank: i + 1,
}));

/** Mid-game reveal: same question as MOCK_QUESTION_OPEN_STATE, now showing answers. */
export const MOCK_QUESTION_REVIEW_STATE: SessionState = (() => {
  if (Q0.type !== 'quiz') throw new Error('MOCK_QUESTION_REVIEW_STATE expects Q0 to be a quiz');
  const correctId = Q0.options.find((o) => o.isCorrect)!.id;
  // Realistic pick breakdown that the kiosk Review screen can render
  // properly: the correct option draws Mads + Tom; one wrong option
  // becomes the popular wrong-guess; the third gets one straggler;
  // the fourth gets nobody (so the bar + sticker has the "nobody
  // picked this" shape too).
  const picksByOption: Record<number, string[]> = {
    0: ['p-1', 'p-5'],
    1: ['p-2', 'p-3', 'p-4'],
    2: [],
    3: [],
  };
  return {
    ...MOCK_QUESTION_OPEN_STATE,
    status: 'question_review',
    version: 9,
    leaderboard: LEADERBOARD_AFTER_Q1,
    reveal: {
      type: 'quiz',
      correctOptionId: correctId,
      distribution: Q0.options.map((o, i) => {
        const playerIds = picksByOption[i] ?? [];
        return { optionId: o.id, count: playerIds.length, playerIds };
      }),
      author: { displayName: 'Mads' },
    },
  };
})();

/** Between-questions interstitial: standings shown, no current Q. */
export const MOCK_BETWEEN_STATE: SessionState = {
  sessionId: MOCK_SESSION_ID,
  pin: '123456',
  status: 'between',
  theme: 'default',
  version: 11,
  progressionMode: 'kiosk',
  // Question 1 is done; the kiosk caches `currentIndex` so the next
  // QuestionCard can render "Question 2 of N" in the lead-in.
  currentIndex: 0,
  totalQuestions: MOCK_QUESTIONS.length,
  players: PLAYERS,
  leaderboard: LEADERBOARD_AFTER_Q1,
};

/** Terminal state: game over, leaderboard frozen. */
export const MOCK_ENDED_STATE: SessionState = {
  sessionId: MOCK_SESSION_ID,
  pin: '123456',
  status: 'ended',
  theme: 'default',
  version: 42,
  progressionMode: 'kiosk',
  totalQuestions: MOCK_QUESTIONS.length,
  players: PLAYERS,
  leaderboard: LEADERBOARD_AFTER_Q1,
};

/** Lobby with one player promoted to advance — kiosk operator's
 *  "hand the remote to Aunt Lise" scenario. */
export const MOCK_LOBBY_PROMOTED_STATE: SessionState = {
  ...MOCK_LOBBY_STATE,
  advanceDelegatePlayerId: 'p-2',
};

/** Ended state with 30 players so the kiosk's auto-scrolling
 *  full-leaderboard exercises its overflow path. */
export const MOCK_ENDED_LARGE_STATE: SessionState = (() => {
  const synthetic = Array.from({ length: 27 }, (_, i) => ({
    id: `synth-${i}`,
    nickname:
      [
        'Lasse',
        'Sofie',
        'Esben',
        'Maja',
        'Magnus',
        'Freja',
        'Ida',
        'Mikkel',
        'Liva',
        'Albert',
        'Vilma',
        'Tobias',
        'Karla',
        'Asger',
        'Vera',
        'Storm',
        'Alma',
        'Frida',
        'Lukas',
        'Ellen',
        'Otto',
        'Nora',
        'Hugo',
        'Agnes',
        'Vilfred',
        'Selma',
        'Birk',
      ][i] ?? `Player ${i + 6}`,
    avatar: ['fox', 'panda', 'tiger', 'wolf', 'owl', 'cat', 'dog', 'frog', 'octopus'][i % 9]!,
    joinedAt: new Date().toISOString(),
  }));
  const allPlayers = [...PLAYERS, ...synthetic];
  const leaderboard = allPlayers.map((p, i) => ({
    playerId: p.id,
    nickname: p.nickname,
    avatar: p.avatar,
    isHost: 'isHost' in p && p.isHost === true,
    // Spread scores from 2400 down to ~50 so the bottom row reads
    // very different from the top.
    score: Math.max(50, 2400 - i * 75),
    rank: i + 1,
  }));
  return {
    sessionId: MOCK_SESSION_ID,
    pin: '123456',
    status: 'ended',
    theme: 'default',
    version: 99,
    progressionMode: 'kiosk',
    totalQuestions: MOCK_QUESTIONS.length,
    players: allPlayers,
    leaderboard,
  };
})();

// ────────────────────── Image-bearing question ──────────────────────
//
// Inline SVG → data URL: avoids a network fetch in Storybook and keeps
// the visual content reproducible across runs. Painted as a chunky
// retro illustration so the kiosk image area is visibly populated.
const SVG_PLACEHOLDER_IMG = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 400">
    <rect width="600" height="400" fill="#f8e2c5"/>
    <circle cx="170" cy="200" r="120" fill="#d76f55"/>
    <polygon points="430,120 540,280 320,280" fill="#3b6e5b"/>
    <rect x="60" y="320" width="480" height="50" fill="#1a1618" rx="10"/>
    <text x="300" y="356" text-anchor="middle" font-family="serif"
          font-size="28" fill="#f8e2c5" font-weight="900">
      kiosk image placeholder
    </text>
  </svg>`,
)}`;

/** Question with an image attached — exercises the QuestionCard
 *  image cap + the still-needs-to-fit-the-options-below layout. */
export const MOCK_QUESTION_OPEN_WITH_IMAGE_STATE: SessionState = {
  ...MOCK_QUESTION_OPEN_STATE,
  currentQuestionKiosk: {
    ...MOCK_QUESTION_OPEN_STATE.currentQuestionKiosk!,
    imageUrl: SVG_PLACEHOLDER_IMG,
  },
};

/** Tall portrait image variant — exercises the height-cap path
 *  separately from the wide-landscape variant (most fixtures). */
const SVG_PORTRAIT_IMG = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 600">
    <rect width="400" height="600" fill="#fef3e6"/>
    <polygon points="200,80 360,500 40,500" fill="#3b6e5b"/>
    <circle cx="200" cy="320" r="80" fill="#d76f55"/>
    <text x="200" y="560" text-anchor="middle" font-family="serif"
          font-size="22" fill="#1a1618" font-weight="900">
      portrait placeholder
    </text>
  </svg>`,
)}`;

// ────────────────────── Worst-case torture tests ──────────────────────
//
// DB caps: `questions.stem` ≤ 120 chars, `question_options.text` ≤ 60
// chars. The kiosk MUST render the worst case at every viewport from
// iPhone SE to 4K TV without a scrollbar. These fixtures use the
// literal max lengths so a Storybook screenshot harness can catch
// any regression at the boundary.

const MAX_STEM_120 =
  // 120 chars exactly — verify with Array.from(s).length === 120.
  "Which embarrassing childhood story would the host say they're least proud of sharing with a friend at dinner — pick one!";

const MAX_OPT_60_A =
  // 60 chars — the supermarket aerobics class incident in full
  "Supermarket aerobics class with a tragically misplaced banana";
const MAX_OPT_60_B =
  // 60 chars
  'The accordion incident on the dreaded school field trip 9';
const MAX_OPT_60_C =
  // 60 chars
  "All of 'Cats' sung at top volume on a regional train ride";
const MAX_OPT_60_D =
  // 60 chars
  "Wore the priest's actual outfit on accident, on Halloween!";

export const MOCK_QUESTION_OPEN_MAXIMAL_STATE: SessionState = (() => {
  const optionIds = ['o-max-0', 'o-max-1', 'o-max-2', 'o-max-3'];
  return {
    ...MOCK_QUESTION_OPEN_STATE,
    currentQuestionKiosk: {
      ...MOCK_QUESTION_OPEN_STATE.currentQuestionKiosk!,
      stem: MAX_STEM_120,
      options: [MAX_OPT_60_A, MAX_OPT_60_B, MAX_OPT_60_C, MAX_OPT_60_D].map((text, i) => ({
        id: optionIds[i]!,
        position: i,
        text,
      })),
    },
    currentQuestionPlayer: {
      ...MOCK_QUESTION_OPEN_STATE.currentQuestionPlayer!,
      options: optionIds.map((id, i) => ({ id, position: i })),
      optionsCount: 4,
    },
  };
})();

/** WORST case: max-length stem + four max-length options + an
 *  image. Compresses everything onto the screen — the toughest fit
 *  in the kiosk. If this fits at iPhone-SE → 4K, every other case
 *  trivially does. */
export const MOCK_QUESTION_OPEN_MAXIMAL_WITH_IMAGE_STATE: SessionState = {
  ...MOCK_QUESTION_OPEN_MAXIMAL_STATE,
  currentQuestionKiosk: {
    ...MOCK_QUESTION_OPEN_MAXIMAL_STATE.currentQuestionKiosk!,
    imageUrl: SVG_PLACEHOLDER_IMG,
  },
};

/** Portrait-image variant of the max-content torture. Tall images
 *  exercise the height cap from the OTHER axis than the default
 *  landscape SVG. */
export const MOCK_QUESTION_OPEN_PORTRAIT_IMAGE_STATE: SessionState = {
  ...MOCK_QUESTION_OPEN_MAXIMAL_STATE,
  currentQuestionKiosk: {
    ...MOCK_QUESTION_OPEN_MAXIMAL_STATE.currentQuestionKiosk!,
    imageUrl: SVG_PORTRAIT_IMG,
  },
};

/** Reveal phase with the max stem + options AND a packed avatar
 *  chip strip per option (12+ players in each tile so the "+more"
 *  overflow pill renders). */
export const MOCK_QUESTION_REVIEW_MAXIMAL_STATE: SessionState = (() => {
  const open = MOCK_QUESTION_OPEN_MAXIMAL_STATE;
  const q = open.currentQuestionKiosk!;
  const opts = q.options ?? [];
  const correctOptionId = opts[1]!.id;
  // Synthesise a 30-player crowd so the avatar strip overflows the
  // 12-chip cap and the "+18 more" pill renders.
  const synthesised = Array.from({ length: 30 }, (_, i) => `synth-p-${i}`);
  return {
    ...open,
    status: 'question_review',
    version: 9,
    leaderboard: LEADERBOARD_AFTER_Q1,
    reveal: {
      type: 'quiz',
      correctOptionId,
      distribution: opts.map((o, i) => {
        const playerIds = i === 1 ? synthesised : synthesised.slice(0, i === 0 ? 8 : i === 2 ? 3 : 0);
        return { optionId: o.id, count: playerIds.length, playerIds };
      }),
    },
    // Inject synthesised players so the kiosk's playerById lookup
    // can find their avatars.
    players: [
      ...PLAYERS,
      ...synthesised.map((id, i) => ({
        id,
        nickname: `Friend ${i + 1}`,
        avatar: ['fox', 'panda', 'tiger', 'wolf', 'owl', 'cat', 'dog'][i % 7]!,
        joinedAt: new Date().toISOString(),
      })),
    ],
  };
})();

export const MOCK_CHAT: ChatMessage[] = [
  {
    id: 'c-1',
    sessionId: MOCK_SESSION_ID,
    playerId: 'p-2',
    nickname: 'Aunt Lise',
    avatar: 'unicorn',
    text: 'When are we starting?? I have wine ready 🍷',
    sentAt: new Date(Date.now() - 60_000).toISOString(),
  },
  {
    id: 'c-2',
    sessionId: MOCK_SESSION_ID,
    playerId: 'p-3',
    nickname: 'Cousin Erik',
    avatar: 'panda',
    text: 'I am going to crush you all',
    sentAt: new Date(Date.now() - 30_000).toISOString(),
  },
];

export const MOCK_HOST_PLAYER: Player = {
  id: 'p-1',
  sessionId: MOCK_SESSION_ID,
  nickname: 'Mads (host)',
  avatar: 'fox',
  score: 800,
  rank: 1,
  hostUserId: MOCK_USER.id,
  joinedAt: new Date().toISOString(),
};

export const MOCK_SESSIONS: SessionSummary[] = [
  {
    id: MOCK_SESSION_ID,
    eventId: EVENT_A.id,
    pin: '418720',
    status: 'lobby',
    createdAt: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
    playerCount: PLAYERS.length,
  },
];

// ────────────────────── True / False fixtures ──────────────────────

const TF_QUESTION: QuestionHost = {
  id: 'qqqqqqqq-qqqq-4qqq-8qqq-tftftftftftf',
  eventId: EVENT_A.id,
  type: 'tf',
  stem: 'Sara has never broken a bone.',
  position: 5,
  options: [
    {
      id: 'oooooooo-oooo-4ooo-8ooo-tftftftf0001',
      position: 0,
      text: 'True',
      isCorrect: false,
    },
    {
      id: 'oooooooo-oooo-4ooo-8ooo-tftftftf0002',
      position: 1,
      text: 'False',
      isCorrect: true,
    },
  ],
  createdAt: new Date().toISOString(),
  authorDisplayName: 'Mum',
};

export const MOCK_TF_QUESTION = TF_QUESTION;

/** A T/F question mid-round, no answers yet. */
export const MOCK_TF_QUESTION_OPEN_STATE: SessionState = {
  ...MOCK_QUESTION_OPEN_STATE,
  currentQuestionKiosk: {
    questionId: TF_QUESTION.id,
    type: 'tf',
    index: 0,
    total: MOCK_QUESTIONS.length,
    stem: TF_QUESTION.stem,
    options: TF_QUESTION.options.map((o) => ({
      id: o.id,
      position: o.position,
      text: o.text,
    })),
    durationMs: 15000,
    startedAt: new Date().toISOString(),
    endsAt: new Date(Date.now() + 15000).toISOString(),
  },
  currentQuestionPlayer: {
    questionId: TF_QUESTION.id,
    type: 'tf',
    index: 0,
    total: MOCK_QUESTIONS.length,
    optionsCount: 2,
    options: TF_QUESTION.options.map((o) => ({ id: o.id, position: o.position })),
    durationMs: 15000,
    startedAt: new Date().toISOString(),
    endsAt: new Date(Date.now() + 15000).toISOString(),
  },
};

/** T/F with a heavy lean towards True (most players voted true). */
export const MOCK_TF_QUESTION_OPEN_MOSTLY_TRUE_STATE: SessionState = {
  ...MOCK_TF_QUESTION_OPEN_STATE,
  answeredCount: 18,
};

/** T/F reveal: False was correct, players see who picked what. */
export const MOCK_TF_QUESTION_REVIEW_STATE: SessionState = {
  ...MOCK_TF_QUESTION_OPEN_STATE,
  status: 'question_review',
  leaderboard: LEADERBOARD_AFTER_Q1,
  reveal: {
    type: 'tf',
    correctOptionId: TF_QUESTION.options[1]!.id, // 'False' is correct
    distribution: [
      {
        optionId: TF_QUESTION.options[0]!.id,
        count: 3,
        playerIds: ['p-2', 'p-3', 'p-5'],
      },
      {
        optionId: TF_QUESTION.options[1]!.id,
        count: 2,
        playerIds: ['p-1', 'p-4'],
      },
    ],
  },
};

/** Question with an image attached — exercises the T/F layout with
 *  the optional image row, the same way MCQ images do. */
export const MOCK_TF_QUESTION_OPEN_WITH_IMAGE_STATE: SessionState = {
  ...MOCK_TF_QUESTION_OPEN_STATE,
  currentQuestionKiosk: {
    ...MOCK_TF_QUESTION_OPEN_STATE.currentQuestionKiosk!,
    imageUrl: SVG_PLACEHOLDER_IMG,
  },
};

/** Torture: max-length T/F stem (120 chars) — verifies the kiosk
 *  fits even when the prompt nearly fills the card. */
export const MOCK_TF_QUESTION_OPEN_MAXIMAL_STATE: SessionState = {
  ...MOCK_TF_QUESTION_OPEN_STATE,
  currentQuestionKiosk: {
    ...MOCK_TF_QUESTION_OPEN_STATE.currentQuestionKiosk!,
    stem: MAX_STEM_120,
  },
  currentQuestionPlayer: {
    ...MOCK_TF_QUESTION_OPEN_STATE.currentQuestionPlayer!,
  },
};

// ────────────────────── Match-pairs fixtures ──────────────────────

const MATCH_PAIRS_4 = [
  {
    id: 'mp-1-uuid-4xxx-8xxx-pppppppppppppp1',
    position: 0,
    leftText: 'France',
    rightText: 'Paris',
  },
  {
    id: 'mp-2-uuid-4xxx-8xxx-pppppppppppppp2',
    position: 1,
    leftText: 'Spain',
    rightText: 'Madrid',
  },
  {
    id: 'mp-3-uuid-4xxx-8xxx-pppppppppppppp3',
    position: 2,
    leftText: 'Italy',
    rightText: 'Rome',
  },
  {
    id: 'mp-4-uuid-4xxx-8xxx-pppppppppppppp4',
    position: 3,
    leftText: 'Germany',
    rightText: 'Berlin',
  },
];

const MATCH_QUESTION: QuestionHost = {
  id: 'qqqqqqqq-qqqq-4qqq-8qqq-matchmatch01',
  eventId: EVENT_A.id,
  type: 'match',
  stem: 'Match each country to its capital',
  position: 6,
  pairs: MATCH_PAIRS_4,
  createdAt: new Date().toISOString(),
  authorDisplayName: 'Mads',
};

export const MOCK_MATCH_QUESTION = MATCH_QUESTION;

/** Question_open: 4 pairs, no answers yet. */
export const MOCK_MATCH_QUESTION_OPEN_STATE: SessionState = {
  ...MOCK_QUESTION_OPEN_STATE,
  currentQuestionKiosk: {
    questionId: MATCH_QUESTION.id,
    type: 'match',
    index: 0,
    total: MOCK_QUESTIONS.length,
    stem: MATCH_QUESTION.stem,
    pairs: MATCH_QUESTION.pairs,
    durationMs: 30000,
    startedAt: new Date().toISOString(),
    endsAt: new Date(Date.now() + 30000).toISOString(),
  },
  currentQuestionPlayer: {
    questionId: MATCH_QUESTION.id,
    type: 'match',
    index: 0,
    total: MOCK_QUESTIONS.length,
    pairs: MATCH_QUESTION.pairs,
    durationMs: 30000,
    startedAt: new Date().toISOString(),
    endsAt: new Date(Date.now() + 30000).toISOString(),
  },
};

/** Mid-round: 16 of 20 players have completed. */
export const MOCK_MATCH_QUESTION_OPEN_MOSTLY_DONE_STATE: SessionState = {
  ...MOCK_MATCH_QUESTION_OPEN_STATE,
  answeredCount: 16,
};

/** Reveal: clean — everyone got it right, no mistake lines. */
export const MOCK_MATCH_QUESTION_REVIEW_CLEAN_STATE: SessionState = {
  ...MOCK_MATCH_QUESTION_OPEN_STATE,
  status: 'question_review',
  leaderboard: LEADERBOARD_AFTER_Q1,
  reveal: {
    type: 'match',
    correctOptionId: null,
    distribution: [],
    pairs: MATCH_PAIRS_4,
    // Every left correctly paired with its own right — empty
    // rightChoices yields no mistake lines.
    matchDistribution: MATCH_PAIRS_4.map((p) => ({
      leftPairId: p.id,
      rightChoices: [{ rightPairId: p.id, count: 5 }],
    })),
  },
};

/** Reveal: messy — 30-player session with two strong mis-pair
 *  clusters (France ↔ Berlin, Germany ↔ Paris). Stresses the
 *  faded mistake-line overlay on the kiosk. */
export const MOCK_MATCH_QUESTION_REVIEW_MESSY_STATE: SessionState = {
  ...MOCK_MATCH_QUESTION_OPEN_STATE,
  status: 'question_review',
  leaderboard: LEADERBOARD_AFTER_Q1,
  reveal: {
    type: 'match',
    correctOptionId: null,
    distribution: [],
    pairs: MATCH_PAIRS_4,
    matchDistribution: [
      // France: 18 correct (Paris), 12 wrong (Berlin)
      {
        leftPairId: MATCH_PAIRS_4[0]!.id,
        rightChoices: [
          { rightPairId: MATCH_PAIRS_4[0]!.id, count: 18 },
          { rightPairId: MATCH_PAIRS_4[3]!.id, count: 12 },
        ],
      },
      // Spain: clean — everyone got it right.
      {
        leftPairId: MATCH_PAIRS_4[1]!.id,
        rightChoices: [{ rightPairId: MATCH_PAIRS_4[1]!.id, count: 30 }],
      },
      // Italy: clean.
      {
        leftPairId: MATCH_PAIRS_4[2]!.id,
        rightChoices: [{ rightPairId: MATCH_PAIRS_4[2]!.id, count: 30 }],
      },
      // Germany: 18 correct (Berlin), 12 wrong (Paris) — the inverse
      // of the France/Berlin confusion.
      {
        leftPairId: MATCH_PAIRS_4[3]!.id,
        rightChoices: [
          { rightPairId: MATCH_PAIRS_4[3]!.id, count: 18 },
          { rightPairId: MATCH_PAIRS_4[0]!.id, count: 12 },
        ],
      },
    ],
  },
};

/** Torture: 5 pairs (the max). */
const MATCH_PAIRS_5 = [
  ...MATCH_PAIRS_4,
  {
    id: 'mp-5-uuid-4xxx-8xxx-pppppppppppppp5',
    position: 4,
    leftText: 'Portugal',
    rightText: 'Lisbon',
  },
];

export const MOCK_MATCH_QUESTION_OPEN_5_PAIRS_STATE: SessionState = {
  ...MOCK_MATCH_QUESTION_OPEN_STATE,
  currentQuestionKiosk: {
    ...MOCK_MATCH_QUESTION_OPEN_STATE.currentQuestionKiosk!,
    pairs: MATCH_PAIRS_5,
  },
  currentQuestionPlayer: {
    ...MOCK_MATCH_QUESTION_OPEN_STATE.currentQuestionPlayer!,
    pairs: MATCH_PAIRS_5,
  },
};

/** Torture: 5 pairs + 40-char text on every cell (the longest text
 *  the schema allows). Verifies the board fits even at the max. */
const MATCH_PAIRS_TORTURE = [
  {
    id: 'mp-t1-uid-4xxx-8xxx-pppppppppppppt1',
    position: 0,
    leftText: 'A really long question prompt for testing',
    rightText: 'And an equally long matching answer here',
  },
  {
    id: 'mp-t2-uid-4xxx-8xxx-pppppppppppppt2',
    position: 1,
    leftText: 'Another full-length 40-character cell t',
    rightText: 'Paired with a 40-character right side h',
  },
  {
    id: 'mp-t3-uid-4xxx-8xxx-pppppppppppppt3',
    position: 2,
    leftText: 'Third long prompt for the match-pairs te',
    rightText: 'Third long matching answer for testing v',
  },
  {
    id: 'mp-t4-uid-4xxx-8xxx-pppppppppppppt4',
    position: 3,
    leftText: 'Fourth long prompt for the match-pairs t',
    rightText: 'Fourth long matching answer for testing ',
  },
  {
    id: 'mp-t5-uid-4xxx-8xxx-pppppppppppppt5',
    position: 4,
    leftText: 'Fifth long prompt for the match-pairs te',
    rightText: 'Fifth long matching answer for testing v',
  },
];

export const MOCK_MATCH_QUESTION_OPEN_TORTURE_STATE: SessionState = {
  ...MOCK_MATCH_QUESTION_OPEN_STATE,
  currentQuestionKiosk: {
    ...MOCK_MATCH_QUESTION_OPEN_STATE.currentQuestionKiosk!,
    stem: MAX_STEM_120,
    pairs: MATCH_PAIRS_TORTURE,
  },
  currentQuestionPlayer: {
    ...MOCK_MATCH_QUESTION_OPEN_STATE.currentQuestionPlayer!,
    pairs: MATCH_PAIRS_TORTURE,
  },
};

// ────────────────────── Numeric / slider fixtures ──────────────────────

const NUMERIC_QUESTION: QuestionHost = {
  id: 'qqqqqqqq-qqqq-4qqq-8qqq-numericnum01',
  eventId: EVENT_A.id,
  type: 'numeric',
  stem: 'How many people live in Copenhagen (millions)?',
  position: 7,
  numericMin: 0,
  numericMax: 5,
  numericStep: 0.1,
  numericAnswer: 1.4,
  numericUnit: 'M',
  numericTolerancePct: 25,
  createdAt: new Date().toISOString(),
  authorDisplayName: 'Mads',
};

export const MOCK_NUMERIC_QUESTION = NUMERIC_QUESTION;

/** Question_open: fresh, no answers yet. */
export const MOCK_NUMERIC_QUESTION_OPEN_STATE: SessionState = {
  ...MOCK_QUESTION_OPEN_STATE,
  currentQuestionKiosk: {
    questionId: NUMERIC_QUESTION.id,
    type: 'numeric',
    index: 0,
    total: MOCK_QUESTIONS.length,
    stem: NUMERIC_QUESTION.stem,
    numericMin: 0,
    numericMax: 5,
    numericStep: 0.1,
    numericUnit: 'M',
    durationMs: 30000,
    startedAt: new Date().toISOString(),
    endsAt: new Date(Date.now() + 30000).toISOString(),
  },
  currentQuestionPlayer: {
    questionId: NUMERIC_QUESTION.id,
    type: 'numeric',
    index: 0,
    total: MOCK_QUESTIONS.length,
    numericMin: 0,
    numericMax: 5,
    numericStep: 0.1,
    numericUnit: 'M',
    durationMs: 30000,
    startedAt: new Date().toISOString(),
    endsAt: new Date(Date.now() + 30000).toISOString(),
  },
};

/** Mid-round: ~half of 20 players have submitted. */
export const MOCK_NUMERIC_QUESTION_OPEN_MID_STATE: SessionState = {
  ...MOCK_NUMERIC_QUESTION_OPEN_STATE,
  answeredCount: 11,
};

// Synthesize 30 player submissions spanning the range, with most
// clustered near the correct answer (1.4) and a long tail. The kiosk
// reveal pins each player's avatar at their value; we re-use the
// existing PLAYERS roster (which has 30+ entries) to source ids +
// avatars.
function makeNumericSubmissions(values: number[]) {
  return values.map((value, i) => {
    const p = PLAYERS[i % PLAYERS.length]!;
    return { playerId: p.id, value };
  });
}

const NUMERIC_VALUES_NORMAL = [
  // Cluster around the answer 1.4.
  1.4, 1.4, 1.3, 1.5, 1.5, 1.2, 1.6, 1.3, 1.4, 1.7,
  // Off-by-tolerance band.
  1.1, 1.8, 1.0, 0.9, 1.9,
  // Wider misses.
  0.5, 0.7, 2.2, 2.5, 0.3,
  // Way off.
  4.5, 4.8, 0.1, 3.9, 4.2,
  // Tail.
  2.7, 3.1, 0.6, 3.5, 1.4,
];

export const MOCK_NUMERIC_QUESTION_REVIEW_STATE: SessionState = {
  ...MOCK_NUMERIC_QUESTION_OPEN_STATE,
  status: 'question_review',
  leaderboard: LEADERBOARD_AFTER_Q1,
  reveal: {
    type: 'numeric',
    correctOptionId: null,
    distribution: [],
    numericRange: {
      min: 0,
      max: 5,
      step: 0.1,
      answer: 1.4,
      tolerancePct: 25,
      unit: 'M',
    },
    numericDistribution: makeNumericSubmissions(NUMERIC_VALUES_NORMAL),
  },
};

// Torture: 30 players all crammed within a 5% sliver around the
// answer. Verifies the stacked-avatar overflow + halo-3 still reads.
const NUMERIC_VALUES_CLUSTERED = Array.from({ length: 30 }, (_, i) =>
  Number((1.3 + (i % 5) * 0.05).toFixed(2)),
);

export const MOCK_NUMERIC_QUESTION_REVIEW_CLUSTERED_STATE: SessionState = {
  ...MOCK_NUMERIC_QUESTION_REVIEW_STATE,
  reveal: {
    ...MOCK_NUMERIC_QUESTION_REVIEW_STATE.reveal!,
    numericDistribution: makeNumericSubmissions(NUMERIC_VALUES_CLUSTERED),
  },
};

/** Torture: 120-char stem + Everest-style large range integers. */
const NUMERIC_QUESTION_TORTURE: QuestionHost = {
  ...NUMERIC_QUESTION,
  id: 'qqqqqqqq-qqqq-4qqq-8qqq-numericnum02',
  stem: MAX_STEM_120,
  numericMin: 1000,
  numericMax: 10000,
  numericStep: 1,
  numericAnswer: 8849,
  numericUnit: 'm',
  numericTolerancePct: 15,
};

export const MOCK_NUMERIC_QUESTION_OPEN_TORTURE_STATE: SessionState = {
  ...MOCK_NUMERIC_QUESTION_OPEN_STATE,
  currentQuestionKiosk: {
    ...MOCK_NUMERIC_QUESTION_OPEN_STATE.currentQuestionKiosk!,
    stem: NUMERIC_QUESTION_TORTURE.stem,
    numericMin: 1000,
    numericMax: 10000,
    numericStep: 1,
    numericUnit: 'm',
  },
  currentQuestionPlayer: {
    ...MOCK_NUMERIC_QUESTION_OPEN_STATE.currentQuestionPlayer!,
    numericMin: 1000,
    numericMax: 10000,
    numericStep: 1,
    numericUnit: 'm',
  },
};
